import type { EntitySchema } from '@/types/schema';
import { validateRequestBody, checkAccess, addOwnership } from './rules';
import type { RuleSchema, AppRole, AccessContext } from './rules';
import type { RecordStore, StoredRecord, StoredApp } from './stores/RecordStore';
import {
  BackendError,
  type AppListing,
  type AppMember,
  type AppInvite,
  type InviteInput,
  type BackendClient,
  type BackendRecord,
  type PaginatedRecords,
  type PlatformBackend,
  type PublishInput,
} from './types';

interface MockBackendDeps {
  store: RecordStore;
  getUser: () => { userId: string } | null;
  idGen: () => string;
  now: () => string;
}

// Decouple the app's EntitySchema from the shared rules' RuleSchema so the two
// type hierarchies can evolve independently.
function toRuleSchema(schema: EntitySchema): RuleSchema {
  return { name: schema.name, fields: schema.fields, ownership: schema.ownership, ownerField: schema.ownerField };
}

function toRecord(stored: StoredRecord): BackendRecord {
  return { ...stored.data, id: stored.id, createdAt: stored.createdAt, updatedAt: stored.updatedAt };
}

function toListing(app: StoredApp): AppListing {
  return {
    appId: app.appId,
    name: app.name,
    description: app.description,
    icon: app.icon,
    category: app.category,
    creatorId: app.creatorId,
    publishedAt: app.publishedAt,
    // A published personal app is a remix-only clone-me demo (Discover shows a Demo badge + Clone).
    remixOnly: (app.definition as { kind?: string } | undefined)?.kind === 'personal',
  };
}

export class MockBackend implements PlatformBackend {
  constructor(private deps: MockBackendDeps) {}

  /** A per-app record client. */
  getClient(appId: string): BackendClient {
    return {
      // opts.since accepted for type compat — mock does not implement changed-feed semantics
      listRecords: (entity, opts) => this.listRecords(appId, entity, opts),
      getRecord: (entity, id) => this.getRecord(appId, entity, id),
      // meta (clientUpdatedAt / deviceId) accepted for type compat — ignored by the mock
      createRecord: (entity, data, _meta?) => this.createRecord(appId, entity, data),
      updateRecord: (entity, id, data, _meta?) => this.updateRecord(appId, entity, id, data),
      deleteRecord: (entity, id, _meta?) => this.deleteRecord(appId, entity, id),
    };
  }

  get platform(): PlatformBackend {
    return this;
  }

  private async requireSchema(appId: string, entity: string): Promise<EntitySchema> {
    const schema = await this.deps.store.getSchema(appId, entity);
    if (!schema) throw new BackendError(`Entity '${entity}' not found`, 'not_found');
    return schema;
  }

  /** Resolve the caller's per-app role (owner if creator, else membership) so
   *  role-scoped access rules can be enforced. */
  private async ctxFor(appId: string): Promise<AccessContext> {
    const user = this.deps.getUser();
    if (!user) return {};
    const app = await this.deps.store.getApp(appId);
    if (app && app.creatorId === user.userId) return { appRole: 'owner' };
    const m = await this.deps.store.getMember(appId, user.userId);
    return m ? { appRole: m.role as AppRole } : {};
  }

  private async listRecords(appId: string, entity: string, opts?: { limit?: number; cursor?: string }): Promise<PaginatedRecords> {
    const schema = await this.requireSchema(appId, entity);
    const user = this.deps.getUser();
    const ctx = await this.ctxFor(appId);
    const access = checkAccess(toRuleSchema(schema), 'read', user, undefined, ctx);
    if (!access.allowed) throw new BackendError(access.reason ?? 'Forbidden', user ? 'forbidden' : 'unauthorized');

    const res = await this.deps.store.listRecords(appId, entity, opts?.limit ?? 50, opts?.cursor);
    let items = res.items.map(toRecord);
    if (user && schema.ownerField) {
      const ownerField = schema.ownerField;
      // 'user' data is always per-user. 'role' data: owner/editor see all, members see own.
      const scopeToOwn = schema.ownership === 'user' || (schema.ownership === 'role' && ctx.appRole !== 'owner' && ctx.appRole !== 'editor');
      if (scopeToOwn) items = items.filter((r) => r[ownerField] === user.userId);
    }
    return { items, cursor: res.cursor, count: items.length };
  }

  private async getRecord(appId: string, entity: string, id: string): Promise<BackendRecord> {
    const schema = await this.requireSchema(appId, entity);
    const stored = await this.deps.store.getRecord(appId, entity, id);
    if (!stored) throw new BackendError('Record not found', 'not_found');
    const record = toRecord(stored);
    const access = checkAccess(toRuleSchema(schema), 'read', this.deps.getUser(), record, await this.ctxFor(appId));
    if (!access.allowed) throw new BackendError(access.reason ?? 'Forbidden', 'forbidden');
    return record;
  }

  private async createRecord(appId: string, entity: string, data: Record<string, unknown>): Promise<BackendRecord> {
    const schema = await this.requireSchema(appId, entity);
    const user = this.deps.getUser();
    const access = checkAccess(toRuleSchema(schema), 'create', user, undefined, await this.ctxFor(appId));
    if (!access.allowed) throw new BackendError(access.reason ?? 'Forbidden', user ? 'forbidden' : 'unauthorized');

    const errors = validateRequestBody(data, schema.fields);
    if (errors.length) throw new BackendError(`Validation failed: ${errors.join(', ')}`, 'validation');

    const withOwner = addOwnership(toRuleSchema(schema), data, user);
    const id = this.deps.idGen();
    const ts = this.deps.now();
    await this.deps.store.putRecord({ id, appId, entity, data: withOwner, createdAt: ts, updatedAt: ts });
    return { id, ...withOwner, createdAt: ts, updatedAt: ts };
  }

  private async updateRecord(appId: string, entity: string, id: string, data: Record<string, unknown>): Promise<BackendRecord> {
    const schema = await this.requireSchema(appId, entity);
    const existing = await this.deps.store.getRecord(appId, entity, id);
    if (!existing) throw new BackendError('Record not found', 'not_found');

    const access = checkAccess(toRuleSchema(schema), 'update', this.deps.getUser(), { id, ...existing.data }, await this.ctxFor(appId));
    if (!access.allowed) throw new BackendError(access.reason ?? 'Forbidden', 'forbidden');

    // Ownership is immutable: never let an update payload reassign the owner.
    const incoming = { ...data };
    if (schema.ownerField) delete incoming[schema.ownerField];

    const errors = validateRequestBody(incoming, schema.fields);
    if (errors.length) throw new BackendError(`Validation failed: ${errors.join(', ')}`, 'validation');

    const ts = this.deps.now();
    const merged = { ...existing.data, ...incoming };
    await this.deps.store.putRecord({ ...existing, data: merged, updatedAt: ts });
    return { id, ...merged, createdAt: existing.createdAt, updatedAt: ts };
  }

  private async deleteRecord(appId: string, entity: string, id: string): Promise<{ deleted: boolean; id: string }> {
    const schema = await this.requireSchema(appId, entity);
    const existing = await this.deps.store.getRecord(appId, entity, id);
    if (!existing) throw new BackendError('Record not found', 'not_found');

    const access = checkAccess(toRuleSchema(schema), 'delete', this.deps.getUser(), { id, ...existing.data }, await this.ctxFor(appId));
    if (!access.allowed) throw new BackendError(access.reason ?? 'Forbidden', 'forbidden');

    await this.deps.store.deleteRecord(appId, entity, id);
    return { deleted: true, id };
  }

  // --- PlatformBackend ---

  async publishApp(input: PublishInput): Promise<AppListing> {
    const user = this.deps.getUser();
    if (!user) throw new BackendError('Authentication required', 'unauthorized');
    const appId = this.deps.idGen();
    const publishedAt = this.deps.now();
    const app: StoredApp = {
      appId,
      name: input.name,
      description: input.description,
      icon: input.icon,
      category: input.category,
      creatorId: user.userId,
      publishedAt,
      definition: input.definition,
      schemas: input.schemas,
    };
    await this.deps.store.putApp(app);
    for (const schema of input.schemas) {
      await this.deps.store.putSchema(appId, schema);
    }
    // The creator is the app's owner (in-app users / role-scoped data).
    await this.deps.store.putMember({ appId, userId: user.userId, role: 'owner', joinedAt: publishedAt });
    return toListing(app);
  }

  async listApps(): Promise<AppListing[]> {
    return (await this.deps.store.listApps()).map(toListing);
  }

  async getApp(appId: string): Promise<AppListing | null> {
    const app = await this.deps.store.getApp(appId);
    return app ? toListing(app) : null;
  }

  async enroll(appId: string): Promise<{ appId: string; enrolled: boolean }> {
    const user = this.deps.getUser();
    if (!user) throw new BackendError('Authentication required', 'unauthorized');
    if (!(await this.deps.store.getApp(appId))) throw new BackendError('App not found', 'not_found');
    await this.deps.store.enroll(user.userId, appId);
    return { appId, enrolled: true };
  }

  async listMyApps(): Promise<AppListing[]> {
    const user = this.deps.getUser();
    if (!user) return [];
    const ids = await this.deps.store.listEnrollments(user.userId);
    const apps = await Promise.all(ids.map((id) => this.getApp(id)));
    return apps.filter((a): a is AppListing => a !== null);
  }

  async unpublish(appId: string): Promise<{ appId: string; unpublished: boolean }> {
    const user = this.deps.getUser();
    if (!user) throw new BackendError('Authentication required', 'unauthorized');
    const app = await this.deps.store.getApp(appId);
    if (!app) throw new BackendError('App not found', 'not_found');
    if (app.creatorId !== user.userId) throw new BackendError('Only the creator can unpublish this app', 'forbidden');
    await this.deps.store.deleteApp(appId);
    return { appId, unpublished: true };
  }

  async reportApp(_appId: string, _reason: string): Promise<{ reported: boolean }> {
    return { reported: true };
  }

  async deleteAccount(): Promise<{ deleted: number }> {
    if (!this.deps.getUser()) throw new BackendError('Authentication required', 'unauthorized');
    return { deleted: 0 }; // mock: no server data to purge
  }

  async subscribe(_appId: string, _email: string): Promise<{ status: string }> {
    return { status: 'pending' }; // mock: no email sent
  }

  async broadcast(_appId: string, _payload: { subject: string; body: string; senderAddress: string }): Promise<{ recipients: number; sent: number }> {
    if (!this.deps.getUser()) throw new BackendError('Authentication required', 'unauthorized');
    return { recipients: 0, sent: 0 };
  }

  // --- Publish to website (mock: state is not persisted; web hosting is AWS-only) ---

  async publishToWeb(_appId: string, opts: { visibility: 'public' | 'password'; password?: string; html?: string; schemas?: unknown[] }): Promise<{ webPublished: boolean; webVisibility: 'public' | 'password'; slug?: string; siteUrl?: string; htmlHosted?: boolean }> {
    return {
      webPublished: true,
      webVisibility: opts.visibility,
      ...(opts.html ? { htmlHosted: true, slug: 'mock-app', siteUrl: 'https://example.test/site/mock-app' } : {}),
    };
  }

  async unpublishFromWeb(_appId: string): Promise<{ webPublished: boolean }> {
    return { webPublished: false };
  }

  async unlockSite(_appId: string, _password: string): Promise<{ token: string } | null> {
    return { token: 'mock-site-token' };
  }

  getAppWithSite(appId: string, _siteToken?: string): Promise<AppListing | null> {
    return this.getApp(appId);
  }

  // --- In-app users (membership + invites) ---

  /** owner/editor may manage; resolves the caller's role for the app. */
  private async requireManager(appId: string, ownerOnly = false): Promise<string> {
    const user = this.deps.getUser();
    if (!user) throw new BackendError('Authentication required', 'unauthorized');
    if (!(await this.deps.store.getApp(appId))) throw new BackendError('App not found', 'not_found');
    const { appRole } = await this.ctxFor(appId);
    const allowed = ownerOnly ? appRole === 'owner' : appRole === 'owner' || appRole === 'editor';
    if (!allowed) throw new BackendError('You do not have permission to manage this app', 'forbidden');
    return user.userId;
  }

  async inviteToApp(appId: string, input: InviteInput): Promise<AppInvite> {
    await this.requireManager(appId);
    if (!input.email && !input.phone) throw new BackendError('An email or phone is required', 'validation');
    const invite: AppInvite = {
      token: this.deps.idGen(),
      appId,
      role: input.role ?? 'member',
      email: input.email,
      phone: input.phone,
      invitedBy: this.deps.getUser()!.userId,
      createdAt: this.deps.now(),
    };
    await this.deps.store.putInvite(invite);
    return invite;
  }

  async acceptInvite(token: string): Promise<{ appId: string; role: AppRole }> {
    const user = this.deps.getUser();
    if (!user) throw new BackendError('Authentication required', 'unauthorized');
    const invite = await this.deps.store.getInvite(token);
    if (!invite) throw new BackendError('Invite not found or already used', 'not_found');
    const role = invite.role as AppRole;
    await this.deps.store.putMember({ appId: invite.appId, userId: user.userId, role, joinedAt: this.deps.now() });
    await this.deps.store.enroll(user.userId, invite.appId); // also show in My Apps
    await this.deps.store.deleteInvite(token);
    return { appId: invite.appId, role };
  }

  async listMembers(appId: string): Promise<AppMember[]> {
    await this.requireManager(appId);
    return (await this.deps.store.listMembers(appId)).map((m) => ({ userId: m.userId, role: m.role as AppRole, joinedAt: m.joinedAt }));
  }

  async setMemberRole(appId: string, userId: string, role: AppRole): Promise<AppMember> {
    await this.requireManager(appId, true);
    const existing = await this.deps.store.getMember(appId, userId);
    if (!existing) throw new BackendError('Not a member of this app', 'not_found');
    const joinedAt = existing.joinedAt;
    await this.deps.store.putMember({ appId, userId, role, joinedAt });
    return { userId, role, joinedAt };
  }

  async removeMember(appId: string, userId: string): Promise<{ appId: string; userId: string; removed: boolean }> {
    await this.requireManager(appId, true);
    const app = await this.deps.store.getApp(appId);
    if (app && app.creatorId === userId) throw new BackendError('The owner cannot be removed', 'forbidden');
    await this.deps.store.removeMember(appId, userId);
    return { appId, userId, removed: true };
  }
}
